import { memo, useEffect, useState } from "react";
import { Card, Button, Toast, Spinner, Container, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWarning } from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";
import { MapPin, Heart, Info } from "lucide-react";

import CopyToClipboardButton from "../CopyToClipboardButton";
import PropertyNotes from "../PropertyNotes";
import { propertiesAPI } from "./PropertiesAPI";
import {
  registrationEndDateToDisplay,
  injectHardCodedRegistrationDates,
  isOnlineOrZeus,
} from "../../utilities/hybridSales";

import RegistrationButton from "./RegistrationButton";
import TimeToggle from "./TimeToggle";
import { hasPlottableCoordinates, hasGeocodableAddress } from "../../utilities/coordinatesUtils";

const PropertiesCard = ({
  property,
  favoriteId,
  handleDetailsClick,
  handleRegisterOrUnregisterClick,
  onMapClick,
  isPropertyFavorite,
  isRegistered,
  getFavoriteButtonLabel,
  getRegisterButtonLabel,
  propIndex,
  originalSaleId,
  userRegistrations,
  removeFavoriteFunc,
  addFavoriteFunc,
}) => {
  const [propertyInfo, setPropertyInfo] = useState(null);
  const [foreclosureSaleId, setForeclosureSaleId] = useState(null);
  const [isFetchingInfo, setIsFetchingInfo] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const [auctionDetail, setAuctionDetail] = useState(
    injectHardCodedRegistrationDates(property.auctionDetail, property.saleId),
  );
 
  const standardizedSaleId = originalSaleId ?? property.saleId;
  const regEndDate = registrationEndDateToDisplay(auctionDetail);
  const isHybrid = isOnlineOrZeus(property) === "Online Registration/In-Person Sale";
 
  // --- Fetch propertyInfo on mount ---
  useEffect(() => {
    if (propertyInfo || isFetchingInfo) return;
    if (standardizedSaleId === null || standardizedSaleId === undefined || isNaN(Number(standardizedSaleId))) {
      setErrorToastMessage("Invalid sale ID for this property.");
      setShowErrorToast(true);
      return;
    }
 
    const fetchInfo = async () => {
      setIsFetchingInfo(true);
      try {
        const details = await propertiesAPI.getPropertyDetail({
          id: property.id,
          propertyId: property.propertyId,
          saleId: Number(standardizedSaleId),
          saleType: property.saleType,
        });
        if (details?.propertyInfo) {
          setPropertyInfo(details.propertyInfo);
        } else {
          setErrorToastMessage("Property details not found.");
          setShowErrorToast(true);
        }
        // Update auctionDetail from the full detail response
        if (details?.auctionDetail) {
          setAuctionDetail(injectHardCodedRegistrationDates(details.auctionDetail, property.saleId));
        }
        // For foreclosures, the real auction sale ID lives in countyDetail.sales
        // since saleId on the property card is always 0
        if (property.saleType === "F" && details?.countyDetail?.sales) {
          const foreclosureSale = details.countyDetail.sales.find((s) => s.saleType === "F");
          if (foreclosureSale?.saleId) {
            setForeclosureSaleId(foreclosureSale.saleId);
          }
        }
      } catch {
        setErrorToastMessage("Failed to fetch property details.");
        setShowErrorToast(true);
      } finally {
        setIsFetchingInfo(false);
      }
    };
 
    fetchInfo();
  }, [property.id, property.propertyId, property.saleType, standardizedSaleId]); // eslint-disable-line react-hooks/exhaustive-deps
 
  const isTaxSale = property.saleTypeDescription !== "Foreclosure";
  const isForeclosureSale = property.saleTypeDescription === "Foreclosure";
  const isCancelled = !!property.cancelled;
 
  // --- Favorite toggle ---
  // Routes through parent props rather than dispatching to Redux directly,
  // so all error handling and toast logic is centralised in Properties.jsx
  const handleFavoriteClick = () => {
    if (isPropertyFavorite(property)) {
      if (favoriteId && removeFavoriteFunc) removeFavoriteFunc();
    } else {
      if (addFavoriteFunc) addFavoriteFunc();
    }
  };
 
  // --- Map availability ---
  // The map modal resolves coordinates itself (stored, or geocoded server-side),
  // so the card only needs to know whether there is anything to locate.
  const isMappable = hasPlottableCoordinates(property.latitude, property.longitude) || hasGeocodableAddress(property);
 
  return (
    <Card className="property-card" style={{ width: "21rem", minHeight: "400px", margin: "2rem" }}>
      <Card.Body>
        {/* Address */}
        <Card.Title className="card-title">
          <span className="truncate">
            {property.address1}, {property.address2}
          </span>
          <CopyToClipboardButton
            textToCopy={`${property.address1}, ${property.city}, ${property.state} ${property.zip}`}
            className="copy-button"
          />
        </Card.Title>
 
        {/* City, State, Zip */}
        <Card.Subtitle className="mb-2 text-muted truncate">
          {`${property.city}, ${property.state} ${property.zip}`}
        </Card.Subtitle>
 
        {/* Hybrid sale warning */}
        {isHybrid && (
          <div
            style={{
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "4px",
              padding: "6px 10px",
              marginBottom: "8px",
              fontSize: "12px",
              color: "#664d03",
            }}
          >
            <FontAwesomeIcon icon={faWarning} /> No on-site registration. Registration must be completed online prior to{" "}
            <b>{regEndDate}</b>.
          </div>
        )}
 
        {/* Tax sale fields */}
        {isTaxSale && (
          <>
            <div className="card-text-row">
              <span className="card-text-label">Sale ID:</span>
              <span className="card-text-value truncate">
                {property.displaySaleId || "N/A"} <CopyToClipboardButton textToCopy={property.displaySaleId || "N/A"} />
              </span>
            </div>
            <div className="card-text-row">
              <span className="card-text-label">Parcel #:</span>
              <div className="card-text-value">
                {property.propertyId ? (
                  property.propertyId.split(" / ").map((parcel, idx) => (
                    <div className="truncate" key={idx}>
                      {parcel} <CopyToClipboardButton textToCopy={parcel} />
                    </div>
                  ))
                ) : (
                  <span>N/A</span>
                )}
              </div>
            </div>
          </>
        )}
 
        {/* Foreclosure-specific fields */}
        {isForeclosureSale && propertyInfo && (
          <>
            <div className="card-text-row">
              <span className="card-text-label">Cause #:</span>
              <span className="card-text-value truncate">
                {propertyInfo.causeNumber || "N/A"}
                <CopyToClipboardButton textToCopy={propertyInfo.causeNumber || "N/A"} />
              </span>
            </div>
            <div className="card-text-row">
              <span className="card-text-label">Defendant:</span>
              <span className="card-text-value truncate">
                {propertyInfo.defendant || "N/A"} <CopyToClipboardButton textToCopy={propertyInfo.defendant || "N/A"} />
              </span>
            </div>
          </>
        )}
 
        {/* Sale date */}
        <div className="card-text-row">
          <span className="card-text-label">Sale Date:</span>
          <span className="card-text-value truncate">{property.auctionDate || "N/A"}</span>
        </div>
 
        {/* Time toggle */}
        <TimeToggle property={property} />
 
        {/* Status */}
        <div className="card-text-row">
          <span className="card-text-label">Status:</span>
          <span className="card-text-value truncate">{property.saleStatusDescription || "N/A"}</span>
        </div>
 
        {/* Sale type */}
        <div className="card-text-row">
          <span className="card-text-label">Sale Type:</span>
          <span className="card-text-value truncate">{property.saleTypeDescription || "N/A"}</span>
        </div>
 
        {/* Method */}
        <div className="card-text-row">
          <span className="card-text-label">Method:</span>
          <span className="card-text-value truncate">{isHybrid ? "In-Person Sale" : property.type || "N/A"}</span>
        </div>
 
        {/* Notes */}
        <PropertyNotes
          propertyId={property.propertyId}
          state={property.state}
          county={property.county}
          address={property.address1}
          saleTypeDescription={property.saleTypeDescription}
          address1={property.address1}
        />
 
        {/* Action buttons */}
        <div className="d-flex flex-column gap-2 mt-3 propertyCardActions" aria-label="Property Card actions">
          <Container className="w-100 justify-content-between">
            <Row className="mb-2">
              <Col xs={6}>
                <RegistrationButton
                  property={property}
                  isRegistered={isRegistered}
                  standardizedSaleId={standardizedSaleId}
                  effectiveSaleId={
                    property.saleType === "F" ? foreclosureSaleId ?? standardizedSaleId : standardizedSaleId
                  }
                  auctionDetail={auctionDetail}
                  isFetchingInfo={isFetchingInfo || (property.saleType === "F" && !foreclosureSaleId)}
                  propertyInfo={propertyInfo}
                  onRegisterClick={handleRegisterOrUnregisterClick}
                  onError={(msg) => {
                    setErrorToastMessage(msg);
                    setShowErrorToast(true);
                  }}
                  isCancelled={isCancelled}
            />
              </Col>
 
              <Col xs={6}>
                <Button
                  className="flex-fill w-100 d-flex align-items-center justify-content-start gap-3"
                  variant={isPropertyFavorite(property) ? "success" : "outline-success"}
                  onClick={handleFavoriteClick}
                  aria-label={isPropertyFavorite(property) ? "Remove from favorites" : "Add to favorites"}
                >
                  <Heart size={20} fill={isPropertyFavorite(property) ? "currentColor" : "none"} />
                  <span>{isPropertyFavorite(property) ? "Unfavorite" : "Favorite"}</span>
                </Button>
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <Button
                  className="flex-fill w-100 d-flex align-items-center justify-content-start gap-3 px-2 cardButton"
                  onClick={(e) => handleDetailsClick(property, propertyInfo, e)}
                  disabled={isFetchingInfo || !propertyInfo}
                  aria-label={isFetchingInfo ? "Loading property details" : "View property details"}
                >
                  {isFetchingInfo ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <Info size={20} />
                      <span>Details</span>
                    </>
                  )}
                </Button>
              </Col>
              <Col xs={6}>
                <Button
                  className="flex-fill w-100 d-flex align-items-center justify-content-start gap-3 px-2 cardButton"
                  onClick={(e) => onMapClick(property, propertyInfo, e)}
                  aria-label={isMappable ? "View map" : "Map unavailable: no address on file"}
                  title={isMappable ? undefined : "No address available to map"}
                  disabled={!isMappable}
                >
                  <MapPin size={20} />
                  <span>Map</span>
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </Card.Body>
 
      {/* Error toast */}
      <Toast
        onClose={() => setShowErrorToast(false)}
        show={showErrorToast}
        delay={5000}
        autohide
        bg="danger"
        style={{ position: "absolute", top: "10px", right: "10px", minWidth: "200px" }}
      >
        <Toast.Header>
          <strong className="me-auto">Error</strong>
        </Toast.Header>
        <Toast.Body>{errorToastMessage}</Toast.Body>
      </Toast>
    </Card>
  );
};
 
PropertiesCard.propTypes = {
  property: PropTypes.shape({
    id: PropTypes.number,
    propertyId: PropTypes.string.isRequired,
    saleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    address1: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired,
    saleTypeDescription: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    date: PropTypes.string,
    time: PropTypes.string,
    county: PropTypes.string,
    auctionDate: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    auctionDetail: PropTypes.object,
    displaySaleId: PropTypes.string,
    saleStatusDescription: PropTypes.string,
  }).isRequired,
  favoriteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleDetailsClick: PropTypes.func.isRequired,
  handleRegisterOrUnregisterClick: PropTypes.func.isRequired,
  isPropertyFavorite: PropTypes.func.isRequired,
  isRegistered: PropTypes.bool.isRequired,
  getFavoriteButtonLabel: PropTypes.func.isRequired,
  getRegisterButtonLabel: PropTypes.func.isRequired,
  propIndex: PropTypes.number.isRequired,
  originalSaleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userRegistrations: PropTypes.array,
  removeFavoriteFunc: PropTypes.func,
  addFavoriteFunc: PropTypes.func,
};
 
export default memo(PropertiesCard);